import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import { filter, find, concat, isEmpty, isArray, map, includes } from "lodash";

import { AutoComplete, AutoCompleteLoading } from "components/inputs";
import { translateTermOptions } from "app/main/codeSet/actions/codeSet.actions";
import { getSignedInOrgUnit, isEnableCIRS } from "app/auth/store/reducers/user.reducer";
import { getHideHealthLinkOnlyTypeCodes } from "app/auth/store/reducers/system-configuration";
import useSpecialtiesCodeSetValues from "app/main/specialtyProfiles/hooks/useSpecialtiesCodeSetValues";

/**
 * Ensures previously-selected values remain visible in the dropdown even when
 * they are no longer in the current filtered option list.
 *
 * Problem: A value was selected (e.g., on a form or search filter), but the
 * available options have since changed — the value may have been deprecated,
 * filtered out (e.g., marked as CIRS-only, hidden on search), or removed from
 * the specialty's available codes. The dropdown no longer includes it, so the
 * AutoComplete cannot display the current selection correctly.
 *
 * Solution: This function checks if each selected value exists in the dropdown
 * options. If not, it looks up the value in the full unfiltered code set and
 * adds it back as a disabled option. This way:
 * - The current selection displays correctly
 * - The user can see what was previously selected
 * - The user can clear it and pick a valid option, but cannot re-select it
 *
 * @param {Array} options - The current dropdown options (already filtered)
 * @param {Array|*} selectedValue - The currently selected value(s) from form state
 * @param {Array} allCodeSetValues - The full unfiltered code set (used to look up display names)
 * @param {boolean} isTermSelector - Whether the selector uses term codes (code) or GUIDs (id) as values
 * @returns {Array} The options array, with any missing selected values added as disabled entries
 */
const appendMissingSelectedOptions = (options, selectedValue, allCodeSetValues, isTermSelector) => {
  if (isEmpty(selectedValue)) {
    return options;
  }

  let result = options;
  const valuesToCheck = isArray(selectedValue) ? selectedValue : [selectedValue];

  valuesToCheck.forEach(value => {
    const alreadyInOptions = find(result, o => o.value === value);
    if (!alreadyInOptions) {
      const existingOption = find(allCodeSetValues, x => (isTermSelector ? x.code === value : x.id === value));
      if (existingOption) {
        result = concat(result, {
          value: isTermSelector ? existingOption.code : existingOption.id,
          label: existingOption.displayName,
          disabled: true,
          settings: existingOption.propertiesJson,
        });
      }
    }
  });

  return result;
};

const SpecialtiesCodeSetSelector = ({
  label,
  specialtyIds,
  codeSetTypeCode,
  multiple,
  forceLoad,
  isTermSelector = false,
  loadOptions,
  allowHideHealthLinkOnly = false,
  allowHideCirsOnly = false,
  allowHideOnForms = false,
  allowHideOnSearch = false,
  ...other
}) => {
  const orgUnit = useSelector(getSignedInOrgUnit);
  const hideHealthLinkOnlyTypeCodes = useSelector(getHideHealthLinkOnlyTypeCodes);
  const enableCIRS = useSelector(isEnableCIRS);

  const isReloadOptions = (loadOptions || specialtyIds) && codeSetTypeCode;

  const { field: { name, value: selectedValue }, form: { setFieldValue } } = other;

  const handleDataLoaded = data => {
    if (!isEmpty(selectedValue)) {
      const specialtiesCodeSetValues = map(data, x => (isTermSelector ? x.code : x.id));
      if (isArray(selectedValue)) {
        const updatedSelectedValues = filter(selectedValue, x => includes(specialtiesCodeSetValues, x));
        setFieldValue(name, updatedSelectedValues);
      } else {
        const updatedSelectedValues = includes(specialtiesCodeSetValues, selectedValue) ? selectedValue : null;
        setFieldValue(name, updatedSelectedValues);
      }
    }
  };

  const { loading, codeSetValues: localCodeSetValues } = useSpecialtiesCodeSetValues({
    orgUnitId: orgUnit.id,
    specialtyIds,
    codeSetTypeCode,
    shouldLoad: isReloadOptions,
    onDataLoaded: handleDataLoaded,
  });

  // Compute filtered and transformed options
  const options = useMemo(() => {
    let filteredOptions = filter(localCodeSetValues, x => x.codeSetTypeCode === codeSetTypeCode) || [];

    if (!enableCIRS && allowHideCirsOnly === true) {
      filteredOptions = filter(filteredOptions, x => (x.propertiesJson?.CirsOnly ?? false) === false);
    }

    if (includes(hideHealthLinkOnlyTypeCodes, codeSetTypeCode) && allowHideHealthLinkOnly === true) {
      filteredOptions = filter(filteredOptions, x => (x.propertiesJson?.HealthLinkOnly ?? false) === false);
    }
    if (allowHideOnForms) {
      filteredOptions = filter(filteredOptions, x => (x.propertiesJson?.HideOnForms ?? false) === false);
    }
    if (allowHideOnSearch) {
      filteredOptions = filter(filteredOptions, x => (x.propertiesJson?.HideOnSearch ?? false) === false);
    }

    let result = [];
    if (isReloadOptions) {
      result = translateTermOptions(filteredOptions, isTermSelector, selectedValue);
    }

    // Append any previously-selected values that are no longer in the filtered options
    result = appendMissingSelectedOptions(result, selectedValue, localCodeSetValues, isTermSelector);

    return result;
  }, [
    localCodeSetValues,
    enableCIRS,
    allowHideCirsOnly,
    hideHealthLinkOnlyTypeCodes,
    codeSetTypeCode,
    allowHideHealthLinkOnly,
    allowHideOnForms,
    allowHideOnSearch,
    isReloadOptions,
    isTermSelector,
    selectedValue,
  ]);

  if (loading) return <AutoCompleteLoading label={label} />;

  const multipleProps = multiple && {
    handleOnChange: (newValue, _name, _setFieldValue) => _setFieldValue(_name, newValue),
  };

  return (
    <AutoComplete
      label={label}
      options={options}
      filterSelectedOptions
      multiple={multiple}
      getOptionDisabled={option => option.disabled}
      {...multipleProps}
      {...other}
    />
  );
};

export default SpecialtiesCodeSetSelector;
